/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
import AuthLogo from '@brands/Auth/assets/loginLogo.svg';
import Logo from '@brands/Auth/assets/logo.svg';
import MobileLogo from '@brands/Auth/assets/mobileLogo.svg';
import { config } from '@brands/config/config';
import DeleteAccount from '@brands/Dashboard/Sidenav/SidenavModals/DeleteAccount';
import { startLinkedCaseCompletion } from '@brands/Dashboard/MultiStage/utils/completeLinkedCase';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getCaseJourneys } from '@brands/services/cases/multiStage';
import { updateEmailAddress } from '@brands/services/identity/updateEmailAddress';
import { updateMe } from '@brands/services/identity/updateMe';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectClientBranding } from '@brands/store/selectors/clientBranding';
import { selectOrganization } from '@brands/store/selectors/organization';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { setNotificationSettings } from '@brands/store/slices/authSlice';
import { setDependentId } from '@brands/store/slices/pageStateSlice';
import { setSidebarState, toggleSidebar } from '@brands/store/slices/sidebarStateSlice';
import { setVCCaseId } from '@brands/store/slices/videoCallCredentialsSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SchoolIcon from '@mui/icons-material/School';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile as mobileDetect } from 'react-device-detect';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { CgProfile } from 'react-icons/cg';
import { FaBell, FaRegBell } from 'react-icons/fa';
import { GoBook } from 'react-icons/go';
import { HiOutlineAcademicCap, HiOutlineLogout } from 'react-icons/hi';
import { MdOutlineEditCalendar } from 'react-icons/md';
import { TbCalendarEvent } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useNotifications, useSubscribe } from '../../hooks';
import { useConversationStatisticsRealtime } from '../../hooks/useConversationStatisticsRealtime';
import { usePatientPlan } from '../../hooks/usePatientPlans';
import { useAppDispatch, useAppSelector } from '../../hooks/useReduxHook';
import { Subscriber } from '../../hooks/useSubscribe';
import useWindowSize from '../../hooks/useWindowSize';
import { getOneCase } from '../../services/cases/getCaseById';
import { CaseType } from '../../services/cases/types/ICase';
import { getUserAvatar } from '../../services/identity/getUserAvatar';
import { UserRoleName } from '../../services/identity/types/UserProfile';
import { acknowledgeNotificationsByUserId } from '../../services/notification/acknowledgeNotificationsByUser';
import { INotification, NotificationType } from '../../services/notification/types/INotification';
import { selectSidebarState } from '../../store/selectors/sidebarState';
import { setMyProfileSidebarState } from '../../store/slices/myProfileSidebarSlice';
import { setSelectedPatient } from '../../store/slices/patientFormSlice';
import { setSelectedPatientByAdmin } from '../../store/slices/selectedPatientSlice';
import Sidebar from '../Dashboard/Components/SideBar/Sidebar';
import addDependentSVG from './Icons/addDependentSVG.svg';
import logoutSVG from './Icons/logoutSVG.svg';
import notificationsSvg from './Icons/notificationsSVG.svg';
import settingsSVG from './Icons/settingsSVG.svg';
import NotificationPopUp from './NotificationPopup/NotificationPopUp';
import Notifications from './Notifications';
import PatientNotifications from './patient/Notifications';
import AccountSettingsModal from './SidenavModals/AccountSettingsModal/AccountSettingsModal';
import ChangeEmailModal from './SidenavModals/ChangeEmailModal/ChangeEmailModal';
import ChangePasswordModal from './SidenavModals/ChangePasswordModal/ChangePasswordModal';
import CreateNewConversationModal from './SidenavModals/CreateNewConversationModal';
import Logout from './SidenavModals/Logout';
import VerifyEmailModal from './SidenavModals/VerifyEmailModal/VerifyEmailModal';
import styles from './styles.module.scss';
import { getNotificationPath, isLinkedCaseActionNotification } from './utils/notification';

type SidenavProps = {
  isVisibleOnMobile: boolean | undefined;
  children?: React.ReactNode;
  isProfileCard?: boolean;
  hideDefaultOptions?: boolean;
  authLoog?: boolean;
  hideModals?: () => void;
};

const Sidenav = ({
  isVisibleOnMobile,
  children = null,
  isProfileCard = false,
  hideDefaultOptions = false,
  authLoog = false,
  hideModals = () => {},
}: SidenavProps): JSX.Element => {
  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768 || mobileDetect;
  const [showSidenav, setShowSidenav] = useState<string>('');
  const [logoutModal, setLogoutModal] = useState(false);
  const [createNewConv, setCreateNewCon] = useState(false);
  const [accountSettingsModal, setAccountSettingsModal] = useState(false);
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [changeEmailModal, setChangeEmailModal] = useState(false);
  const [verifyEmailModal, setVerifyEmailModal] = useState(false);
  const { userInfo: userInfoInContext } = useAppSelector(selectAuth);
  const { isCollapsed } = useAppSelector(selectSidebarState);
  const [closeMenu, setCloseMenu] = useState(isCollapsed);
  const { is_visible: isVisible } = useAppSelector(selectVideoCallCredentials);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);
  const [notificationToggle, setNotificationToggle] = useState(false);
  const { currentOrganization } = useAppSelector(selectOrganization);
  const clientBranding = useAppSelector(selectClientBranding);
  const isPatient = userInfoInContext?.role?.name === UserRoleName.Patient;
  const { group: currentPlan } = usePatientPlan(isPatient);

  const { notifications, unreadCount, loadNextPage, dataLoading, reloadAll, acknowledgeNotification } =
    useNotifications(Boolean(userInfoInContext), userInfoInContext?.role?.name);
  const { subscribe, unsubscribe } = useSubscribe();
  // DO-3975: subscribe to Twilio Conversations Client real-time events
  // (messageAdded, connectionStateChanged→connected) so the Messages
  // sidebar red-dot badge updates the instant a new message lands,
  // including catch-up after a reconnect. See hook docstring for the
  // full three-signal strategy.
  useConversationStatisticsRealtime();

  const userRole = useMemo(() => {
    if (!userInfoInContext) {
      return null;
    }

    return userInfoInContext.role.name;
  }, [userInfoInContext]);

  const handleSidenavContent = (target: string): void => {
    if (showSidenav === target) {
      setShowSidenav('');
    } else {
      setShowSidenav(target);
    }
  };

  useEffect(() => {
    setCloseMenu(isCollapsed);
  }, [isCollapsed]);

  useEffect(() => {
    if (showSidenav === 'profile') {
      dispatch(setMyProfileSidebarState(true));
    } else {
      dispatch(setMyProfileSidebarState(false));
    }
  }, [showSidenav, dispatch]);

  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const location = useLocation();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event: any): void {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSidenav('');
      }
    }
    if (showSidenav) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSidenav]);

  const [avatarIcon, setAvatarIcon] = useState(false);
  const handleAvatarError = (): void => {
    setAvatarIcon(true);
  };

  const handleCloseMenu = (): void => {
    setCloseMenu(!closeMenu);
    setShowSidenav('');
    if (isMobile && isCollapsed) {
      dispatch(setSidebarState(false));
    } else if (!isMobile) {
      dispatch(toggleSidebar());
    }
  };

  const openZendesk = (): void => {
    window.open('https://dentistryone.zendesk.com', '_blank', 'noreferrer');
  };

  const handleNavigate = useCallback(
    (path: string, domain?: string): void => {
      if (domain && domain !== window.location.host) {
        window.location.href = `https://${domain}${path}`;
        return;
      }
      if (location.pathname === path) {
        navigate(0);
      } else {
        roleNavigate(path);
      }
    },
    [location, navigate, roleNavigate],
  );

  const onDeleteAccount = useCallback(() => {
    setAccountSettingsModal(false);
    setDeleteAccountModal(true);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (isVisible) {
        event.preventDefault();
        event.returnValue = '';
        window.close();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isVisible]);

  const onNotification = useCallback(
    async (notification: INotification): Promise<void> => {
      const { type, payload, domain, body } = notification;
      const { case_id: caseId, case_type: caseType } = payload;
      const path = getNotificationPath(notification);
      if (path) {
        handleNavigate(
          path,
          userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
        );
        acknowledgeNotification(notification.id);
        return;
      }

      if (
        userRole === UserRoleName.Patient &&
        config.features.multiStage &&
        isLinkedCaseActionNotification(notification)
      ) {
        try {
          const notificationCase = await getOneCase(Number(caseId));
          // Notifications without a path and case details can omit the journey ID.
          // Resolve the case's patient so guardian notifications open the dependent's journey.
          const journeys = await getCaseJourneys(notificationCase.patient_id);
          const journey = journeys.multi_stage.find((item) =>
            item.cases.some((journeyCase) => String(journeyCase.id) === String(caseId)),
          );
          if (journey) {
            // completeCaseId makes the journey open this case's intake wizard
            // while it still needs patient action, matching the email link.
            handleNavigate(
              `/patient/my-care-journey?patientId=${notificationCase.patient_id}&journeyId=${journey.case_journey_id}&completeCaseId=${caseId}`,
              domain,
            );
            acknowledgeNotification(notification.id);
            return;
          }
        } catch (error: unknown) {
          displayErrorDetails(error);
          return;
        }
      }

      switch (type) {
        case NotificationType.appointment_reminder_hour:
        case NotificationType.appointment_reminder_min:
          if (userRole === UserRoleName.Provider) {
            handleNavigate(`/consultation-page/${caseId}`);
          }

          if (userRole === UserRoleName.Patient) {
            switch (caseType) {
              case 'smile_scan':
                handleNavigate(`/smile-scan-report/${caseId}`, domain);
                break;
              case 'video_call_scheduled':
                handleNavigate(`/video-room/${caseId}`, domain);
                dispatch(setVCCaseId(caseId.toString()));
                break;
              case 'oral_health_coaching':
                handleNavigate(`/video-room/${caseId}`, domain);
                dispatch(setVCCaseId(caseId.toString()));
                break;
              default:
                // handle default case if needed
                break;
            }
          }
          break;
        case NotificationType.ca_vc_joined:
        case NotificationType.case_updated:
          if (userRole === UserRoleName.Provider) {
            handleNavigate(`/consultation-page/${caseId}`);
          }

          if (userRole === UserRoleName.CareAdvisor) {
            handleNavigate(`/consultation-page/${caseId}`);
          }

          if (userRole === UserRoleName.Patient) {
            switch (caseType) {
              case 'smile_scan':
                handleNavigate(`/smile-scan-report/${caseId}`, domain);
                break;
              case 'video_call_scheduled':
                handleNavigate(`/video-room/${caseId}`, domain);
                dispatch(setVCCaseId(caseId.toString()));
                break;
              case 'oral_health_coaching':
                handleNavigate(`/video-room/${caseId}`, domain);
                dispatch(setVCCaseId(caseId.toString()));
                break;
              default:
                // handle default case if needed
                break;
            }
          }
          break;
        case NotificationType.case_created:
        case NotificationType.patient_vc_joined:
        case NotificationType.provider_vc_joined:
        case NotificationType.patient_vc_not_joined:
        case NotificationType.provider_vc_not_joined:
        case NotificationType.instant_vc_not_started:
          if (
            userRole === UserRoleName.Patient ||
            userRole === UserRoleName.Provider ||
            userRole === UserRoleName.Collector
          ) {
            handleNavigate(`/video-room/${caseId}`, domain);
            dispatch(setVCCaseId(caseId.toString()));
          } else {
            handleNavigate(`/consultation-page/${caseId}`);
          }
          break;
        case NotificationType.message:
          if (caseId) {
            handleNavigate(
              `/case-chat/${caseId}/${payload.conversation_sid}`,
              userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
            );
          }
          break;
        case NotificationType.case_completed:
          if (body?.includes('Hooray! Your Oral Health Coaching Report')) {
            handleNavigate(
              `/oral-health-coaching-report/${caseId}`,
              userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
            );
          } else {
            handleNavigate(
              `/oral-health-report/${caseId}`,
              userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
            );
          }
          break;
        case NotificationType.case_canceled:
          if (userRole !== UserRoleName.Patient && userRole !== UserRoleName.Provider) {
            handleNavigate(`/consultation-page/${caseId}`);
          }
          break;
        case NotificationType.cc_reauthorization_failed:
          if (!payload.update_disabled) {
            handleNavigate(
              `/my-care?edit=${caseId}#upcoming_appointments`,
              userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
            );
          }
          break;
        case NotificationType.provider_no_longer_available: {
          const caseTypeString = caseType?.toString();
          const rescheduleRoute =
            caseTypeString === CaseType.oral_health_coaching ? '/select-care-advisor' : '/select-provider';

          roleNavigate(rescheduleRoute, {
            state: {
              RescheduleCase: true,
              caseId: caseId.toString(),
              caseType: caseTypeString,
            },
          });
          break;
        }
        case NotificationType.linked_case_created: {
          const caseTypeString = caseType?.toString();
          const scheduledCaseTypes: string[] = [
            CaseType.video_call_scheduled,
            CaseType.phone_call_scheduled,
            CaseType.oral_health_coaching,
          ];

          if (scheduledCaseTypes.includes(caseTypeString)) {
            const scheduleRoute =
              caseTypeString === CaseType.oral_health_coaching ? '/select-care-advisor' : '/select-provider';

            // The linked case already has an assigned provider/care advisor —
            // fetch it so select-provider/select-care-advisor can filter down
            // to that clinician instead of defaulting to whoever has the
            // soonest open slot.
            let assignedCase;
            try {
              assignedCase = await getOneCase(Number(caseId));
            } catch (error: unknown) {
              displayErrorDetails(error);
            }

            // A staff recipient (the requester of an uninvited patient's
            // program) schedules on that patient's behalf. The scheduler
            // prefers the patient held in Redux over the case's, so replace
            // whoever this user last worked on: availability and any payment
            // profile the reschedule asks for must belong to this case's
            // patient.
            if (assignedCase?.patient && userRole !== UserRoleName.Patient && userRole !== UserRoleName.Dependent) {
              dispatch(setSelectedPatientByAdmin({ selectedPatient: assignedCase.patient }));
              dispatch(
                setSelectedPatient({
                  value: assignedCase.patient.id.toString(),
                  label: assignedCase.patient.full_name,
                }),
              );
            }

            roleNavigate(scheduleRoute, {
              state: {
                RescheduleCase: true,
                caseId: caseId.toString(),
                caseType: caseTypeString,
                providerId: caseTypeString === CaseType.oral_health_coaching ? 0 : assignedCase?.provider_id || 0,
                careAdvisorId: assignedCase?.care_advisor_id || 0,
                patient_current_state: assignedCase?.patient_current_state,
                RescheduleCaseSelectedCarrierId:
                  assignedCase?.insurance?.insurance_membership?.insurance_carrier?.id || null,
              },
            });
          } else if (caseTypeString === CaseType.care_coordination && userRole === UserRoleName.Patient) {
            // The payload's type alone cannot distinguish a root CC from
            // a bundled follow-up. Completion checks the case's intake API.
            let assignedCase;
            try {
              assignedCase = await getOneCase(Number(caseId));
            } catch (error: unknown) {
              displayErrorDetails(error);
            }
            if (assignedCase) {
              await startLinkedCaseCompletion(assignedCase, dispatch, roleNavigate);
            }
          } else if (payload?.needs_intake) {
            // Same wizard flow as the care_coordination branch above, now
            // generalized to every case type — see completeLinkedCase.ts.
            let assignedCase;
            try {
              assignedCase = await getOneCase(Number(caseId));
            } catch (error: unknown) {
              displayErrorDetails(error);
            }
            if (assignedCase) {
              await startLinkedCaseCompletion(assignedCase, dispatch, roleNavigate);
            }
          } else {
            handleNavigate(
              '/my-care',
              userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
            );
          }
          break;
        }
        case NotificationType.fulfillment_approved:
        case NotificationType.fulfillment_declined:
          handleNavigate(
            `/oral-health-report/${caseId}`,
            userRole === UserRoleName.Patient || userRole === UserRoleName.Provider ? domain : undefined,
          );
          break;
        case NotificationType.intake_reminder_5_days:
        case NotificationType.intake_reminder_10_days: {
          let reminderCase;
          try {
            reminderCase = await getOneCase(Number(caseId));
          } catch (error: unknown) {
            displayErrorDetails(error);
          }
          if (reminderCase) {
            await startLinkedCaseCompletion(reminderCase, dispatch, roleNavigate);
          }
          break;
        }
        case NotificationType.schedule_reminder_5_days:
        case NotificationType.schedule_reminder_10_days:
          // Patients with a journey are handled above; keep legacy cases reachable.
          handleNavigate(
            userRole === UserRoleName.Patient ? '/patient/my-care' : '/dashboard',
            userRole === UserRoleName.Patient ? domain : undefined,
          );
          break;
        default:
          break;
      }
      acknowledgeNotification(notification.id);
    },
    [acknowledgeNotification, dispatch, handleNavigate, roleNavigate, userRole],
  );

  const markAllAsRead = async (): Promise<void> => {
    if (unreadCount > 0) {
      await acknowledgeNotificationsByUserId(Number(userInfoInContext.id), userInfoInContext?.role?.name);
      reloadAll();
    }
  };

  const handleChangePassword = useCallback(() => {
    setAccountSettingsModal(false);
    setChangePasswordModal(true);
  }, []);

  const handleChangeEmail = useCallback(() => {
    setAccountSettingsModal(false);
    setChangeEmailModal(true);
  }, []);

  const subscriber: Subscriber = useMemo(
    () => ({
      onMessage: (message: any) => {
        const { notification } = message;

        if (notification) {
          enqueueSnackbar(notification.body, {
            key: notification.id,
            content: (key: any) => (
              <NotificationPopUp id={key} notification={notification} onNotification={onNotification} />
            ),
            autoHideDuration: 10000,
            disableWindowBlurListener: true,
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
          });
        }

        reloadAll();
      },
      commands: ['notification_created'],
    }),
    [onNotification, reloadAll, enqueueSnackbar],
  );

  useEffect(() => {
    const subscriptionId = subscribe(subscriber);

    return () => {
      unsubscribe(subscriptionId);
    };
  }, [subscriber, subscribe, unsubscribe]);

  const handleClick = (): void => {
    if (hideDefaultOptions) {
      hideModals();
      navigate('/');
      return;
    }

    const isAdmin =
      userRole === UserRoleName.Admin ||
      userRole === UserRoleName.SuperAdmin ||
      userRole === UserRoleName.OrganizationAdmin;

    if (isAdmin) {
      roleNavigate('/dashboard');
    } else {
      roleNavigate('/dashboard');
    }
  };

  const getInitials = (firstName: string, lastName: string): string => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const onSubmitChangeEmail = useCallback(
    async (email: string) => {
      try {
        if (userRole === UserRoleName.Collector) {
          return;
        }
        if (!email) {
          throw new Error('Email is required');
        }
        if (userInfoInContext) {
          await updateEmailAddress(userInfoInContext.id, email);
        }
        setChangeEmailModal(false);
        setVerifyEmailModal(true);
      } catch (error) {
        displayErrorDetails(error);
      }
    },
    [userInfoInContext, userRole],
  );

  const updateNotificationSettings = async (smsOptIn: boolean, emailOptIn: boolean): Promise<void> => {
    if (userRole === UserRoleName.Collector) {
      return;
    }

    if (!smsOptIn && !emailOptIn) {
      displayErrorDetails('At least one notification method must be enabled');
      return;
    }

    try {
      await dispatch(setNotificationSettings({ sms: smsOptIn, email: emailOptIn }));
      await updateMe(
        {
          email_opt_in: emailOptIn,
          sms_opt_in: smsOptIn,
        },
        Number(userInfoInContext.id),
        userRole,
        userRole === UserRoleName.Collector ? { collectorEditingSelf: true } : undefined,
      );
      toast.success('Notification preferences saved.', { toastId: 'notification-preferences-saved' });
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  return (
    <div
      className={`${
        userRole === UserRoleName.Patient ||
        userRole === UserRoleName.Provider ||
        userRole === UserRoleName.Collector ||
        userRole === UserRoleName.CareAdvisor
          ? styles.topSidenavPatient
          : styles.topSidenav
      } fs-unmask`}
      style={{ display: (!isVisibleOnMobile && screenSize.width <= 564) || isProfileCard ? 'none' : 'flex' }}
      ref={wrapperRef}
    >
      {userRole === UserRoleName.Patient ||
      userRole === UserRoleName.Provider ||
      userRole === UserRoleName.CareAdvisor ||
      userRole === UserRoleName.Collector ? (
        <div className={styles.sidenav}>
          <div className={authLoog ? styles.sidenavAuthLoogHeader : styles.sidenavHeaderPatient}>
            <div className={styles.sidenavLogo}>
              {hideDefaultOptions ? null : (
                <div className={`fs-unmask ${styles.sidebarIcon}`}>
                  <div className={closeMenu ? `${styles.burgerContainer} ${styles.active}` : styles.burgerContainer}>
                    <div className={styles.burgerTrigger} onClick={handleCloseMenu} />
                    <div className={styles.burgerMenu} />
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() =>
                  hideDefaultOptions
                    ? roleNavigate('/dashboard')
                    : userRole === UserRoleName.Admin ||
                      userRole === UserRoleName.SuperAdmin ||
                      userRole === UserRoleName.OrganizationAdmin
                    ? roleNavigate('/dashboard')
                    : roleNavigate('/dashboard')
                }
                style={{ cursor: 'pointer' }}
                className={
                  config.organization.name !== 'dentistry-one' &&
                  config.organization.name !== 'default' &&
                  config.organization.name !== 'mainely-connection'
                    ? styles.poweredByTextParent
                    : undefined
                }
              >
                {isMobile ? (
                  <img className={styles.logoMobile} src={MobileLogo} alt="Logo" />
                ) : (
                  <img className={styles.logoCorner} src={clientBranding?.logo_url || AuthLogo} alt="Logo" />
                )}
                {(clientBranding?.uses_custom_logo ?? currentOrganization?.uses_custom_logo) && (
                  <span className={styles.poweredByText}>Powered by Dentistry.One</span>
                )}
              </button>
            </div>
            <div className={styles.sidenavIconsPatient}>
              {children}
              {hideDefaultOptions ? null : (
                <>
                  <Sidebar closeMenu={closeMenu} currentPlan={currentPlan} />
                  <div
                    onClick={() => handleSidenavContent('notification')}
                    id="top-right-menu-notifications-button"
                    className="fs-unmask"
                  >
                    <div
                      className={`${styles.sidenavBellPatient} ${
                        showSidenav === 'notification' && styles.sidenavBellActivePatient
                      }`}
                    >
                      <FaBell className={`${styles.sidenavBellIcon}`} />
                      {Boolean(unreadCount) && (
                        <div className={styles.notificationUnreadCountPatient}>
                          {unreadCount >= 0 ? unreadCount : 0}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    onClick={() => handleSidenavContent('profile')}
                    id="top-right-menu-profile-button"
                    className="fs-exclude"
                  >
                    <div
                      className={`fs-exclude ${styles.sidenavTogglePatient} ${
                        showSidenav === 'profile' && styles.sidenavToggleActivePatient
                      }`}
                    >
                      {!avatarIcon ? (
                        <img
                          alt="Avatar"
                          onError={handleAvatarError}
                          className={styles.sidenavImagePatient}
                          src={userInfoInContext ? getUserAvatar(userInfoInContext?.id) : ''}
                        />
                      ) : userInfoInContext?.basic_info?.first_name && userInfoInContext?.basic_info?.last_name ? (
                        // Show initials inside a circle if no image
                        <div className={styles.initialsCircle}>
                          {getInitials(userInfoInContext.basic_info.first_name, userInfoInContext.basic_info.last_name)}
                        </div>
                      ) : (
                        <CgProfile className={styles.defaultIcon} />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {showSidenav === 'notification' && (
            <PatientNotifications
              notifications={notifications}
              onNotification={onNotification}
              markAllAsRead={markAllAsRead}
              loadNextPage={loadNextPage}
              dataLoading={dataLoading}
              setShowSidenav={setShowSidenav}
            />
          )}
          {showSidenav === 'profile' && (
            <div className={`${styles.sidenavBodyProfile} ${styles.sidenavBodyPatient}`}>
              <div className={styles.sidenavProfileInfoPatient}>
                <div className={styles.sidenavProfilePicture}>
                  {!avatarIcon ? (
                    <img
                      alt="Avatar"
                      onError={handleAvatarError}
                      className={styles.sidenavImagePatient}
                      src={userInfoInContext ? getUserAvatar(userInfoInContext?.id) : ''}
                    />
                  ) : userInfoInContext?.basic_info?.first_name && userInfoInContext?.basic_info?.last_name ? (
                    // Show initials inside a circle if no image
                    <div className={styles.initialsCircle}>
                      {getInitials(userInfoInContext.basic_info.first_name, userInfoInContext.basic_info.last_name)}
                    </div>
                  ) : (
                    <CgProfile className={styles.defaultIcon} />
                  )}
                </div>
                <div className={styles.sidenavProfileName}>
                  <span className={`fs-exclude ${styles.userName}`}>
                    {userInfoInContext.full_name ? userInfoInContext.full_name : userInfoInContext.userInfo?.email}
                  </span>
                  <div
                    onClick={() => {
                      if (
                        userRole === UserRoleName.Admin ||
                        userRole === UserRoleName.SuperAdmin ||
                        userRole === UserRoleName.OrganizationAdmin
                      ) {
                        roleNavigate('/my-profile');
                      } else if (userRole === UserRoleName.Patient) {
                        dispatch(setDependentId(null));
                        window.location.replace('/Patient/my-profile');
                      } else {
                        roleNavigate('/my-profile');
                      }
                    }}
                  >
                    <span className={`fs-unmask ${styles.infoHeaderTextPatient}`}>My Profile</span>
                  </div>
                </div>
              </div>
              <hr />
              <div className={styles.sidenavInfo}>
                {userRole === UserRoleName.Patient && (
                  <div
                    className={styles.infoHeaderPatient}
                    onClick={() => roleNavigate(`/my-profile`, { state: { addDependentFromSideNav: true } })}
                  >
                    <img src={addDependentSVG} />

                    <div className={styles.infoHeaderTextPatient}>Add dependent</div>
                  </div>
                )}

                <div
                  className={styles.infoHeaderPatient}
                  onClick={() => {
                    setAccountSettingsModal(!accountSettingsModal);
                    setShowSidenav('');
                  }}
                >
                  <img src={settingsSVG} />
                  <div className={styles.infoHeaderTextPatient}>Account settings</div>
                </div>

                {(userRole === UserRoleName.Provider || userRole === UserRoleName.CareAdvisor) && (
                  <>
                    <a
                      href="https://dentistry.one/resources/"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.infoHeaderPatient}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <SchoolIcon className={styles.logoutIcon} />
                      <div className={styles.infoHeaderTextPatient}>Education</div>
                    </a>

                    <a
                      href="https://dentistry-one-learning.thinkific.com/"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.infoHeaderPatient}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <AutoStoriesIcon className={styles.logoutIcon} />
                      <div className={styles.infoHeaderTextPatient}>Training</div>
                    </a>
                  </>
                )}

                <div
                  className={`${styles.signOutDiv} ${styles.infoHeaderPatient}`}
                  onClick={() => {
                    setLogoutModal(true);
                  }}
                >
                  <img src={logoutSVG} className={styles.logoutIcon} />
                  <div className={styles.infoHeaderTextPatient}>Sign out</div>
                </div>
                <div />
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.sidenav}>
          <div className={authLoog ? styles.sidenavAuthLoogHeader : styles.sidenavHeader}>
            <div className={styles.sidenavLogo}>
              {hideDefaultOptions ? null : (
                <div className={`fs-unmask ${styles.sidebarIcon}`}>
                  <div className={closeMenu ? `${styles.burgerContainer} ${styles.active}` : styles.burgerContainer}>
                    <div className={styles.burgerTrigger} onClick={handleCloseMenu} />
                    <div className={styles.burgerMenu} />
                  </div>
                </div>
              )}
              <button type="button" onClick={handleClick} style={{ cursor: 'pointer' }}>
                <img className={styles.logoCorner} src={clientBranding?.logo_url || AuthLogo} alt="Logo" />
              </button>
            </div>
            <div className={styles.sidenavIcons}>
              {children}
              {hideDefaultOptions ? null : (
                <>
                  <Sidebar closeMenu={closeMenu} currentPlan={currentPlan} />
                  <div
                    onClick={() => handleSidenavContent('notification')}
                    id="top-right-menu-notifications-button"
                    className="fs-unmask"
                  >
                    <div
                      className={`${styles.sidenavBell} ${showSidenav === 'notification' && styles.sidenavBellActive}`}
                    >
                      <FaBell className={styles.sidenavBellIcon} />
                      {Boolean(unreadCount) && (
                        <div className={styles.notificationUnreadCount}>{unreadCount >= 0 ? unreadCount : 0}</div>
                      )}
                    </div>
                  </div>
                  <div
                    onClick={() => handleSidenavContent('profile')}
                    id="top-right-menu-profile-button"
                    className="fs-exclude"
                  >
                    <div
                      className={`fs-exclude ${styles.sidenavToggle} ${
                        showSidenav === 'profile' && styles.sidenavToggleActive
                      }`}
                    >
                      {!avatarIcon ? (
                        <img
                          alt="Avatar"
                          onError={handleAvatarError}
                          className={styles.sidenavImagePatient}
                          src={userInfoInContext ? getUserAvatar(userInfoInContext?.id) : ''}
                        />
                      ) : userInfoInContext?.basic_info?.first_name && userInfoInContext?.basic_info?.last_name ? (
                        <div className={styles.initialsCircle}>
                          {getInitials(userInfoInContext.basic_info.first_name, userInfoInContext.basic_info.last_name)}
                        </div>
                      ) : (
                        <CgProfile className={styles.defaultIcon} />
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div ref={dropdownRef}>
            {showSidenav === 'notification' && (
              <Notifications
                notifications={notifications}
                onNotification={onNotification}
                loadNextPage={loadNextPage}
                dataLoading={dataLoading}
              />
            )}
            {showSidenav === 'profile' && (
              <div className={styles.sidenavBody}>
                <div className={`fs-exclude ${styles.sidenavProfileInfo}`}>
                  <div>
                    {!avatarIcon ? (
                      <img
                        alt="Avatar"
                        onError={handleAvatarError}
                        className={`fs-exclude ${styles.sidenavImage}`}
                        src={userInfoInContext ? getUserAvatar(userInfoInContext?.id) : ''}
                      />
                    ) : (
                      <CgProfile />
                    )}
                  </div>
                  <div className={`${styles.sidenavProfileName} fs-exclude`}>
                    {userInfoInContext.full_name ? userInfoInContext.full_name : userInfoInContext.userInfo?.email}
                  </div>
                </div>
                <hr />
                <div className={styles.sidenavInfo}>
                  <ul style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    <li>
                      <div
                        onClick={() => {
                          if (
                            userRole === UserRoleName.Admin ||
                            userRole === UserRoleName.SuperAdmin ||
                            userRole === UserRoleName.OrganizationAdmin
                          ) {
                            roleNavigate('/my-profile');
                          } else if (userRole === UserRoleName.Patient || userRole === UserRoleName.Provider) {
                            dispatch(setDependentId(null));
                            roleNavigate('/my-profile');
                          } else {
                            roleNavigate('/my-profile');
                          }
                        }}
                      >
                        <CgProfile />
                        <span className={styles.infoHeaderText}>My Profile</span>
                      </div>
                    </li>
                    {userRole !== UserRoleName.CareAdvisor && userRole !== UserRoleName.Collector && (
                      <li>
                        <div onClick={() => setNotificationToggle(!notificationToggle)}>
                          <FaRegBell />
                          <span className={styles.infoHeaderText}>Notification Settings</span>
                        </div>
                        {notificationToggle && (
                          <div className={styles.notificationOptions}>
                            <label className={styles.checkbox} aria-label="Phone">
                              <input
                                type="checkbox"
                                className={`${styles.formCheckInput} form-check-input opacity-100`}
                                id="phone"
                                checked={userInfoInContext.sms_opt_in}
                                onChange={() => {
                                  updateNotificationSettings(
                                    !userInfoInContext.sms_opt_in,
                                    userInfoInContext.email_opt_in,
                                  );
                                }}
                              />
                              <span>
                                <label htmlFor="phone" aria-label="Phone">
                                  Phone
                                </label>
                              </span>
                            </label>
                            <label className={styles.checkbox} aria-label="Email">
                              <input
                                type="checkbox"
                                className={styles.formCheckInput}
                                id="email"
                                checked={userInfoInContext.email_opt_in}
                                onChange={() => {
                                  updateNotificationSettings(
                                    userInfoInContext.sms_opt_in,
                                    !userInfoInContext.email_opt_in,
                                  );
                                }}
                              />
                              <span>
                                <label htmlFor="email">Email</label>
                              </span>
                            </label>
                          </div>
                        )}
                      </li>
                    )}
                  </ul>
                  {!(
                    userRole === UserRoleName.Provider ||
                    userRole === UserRoleName.Collector ||
                    userRole === UserRoleName.Admin ||
                    userRole === UserRoleName.SuperAdmin ||
                    userRole === UserRoleName.OrganizationAdmin
                  ) && (
                    <>
                      <hr />
                      <div className={styles.infoHeader} onClick={() => roleNavigate(`/appointments`)}>
                        <TbCalendarEvent />
                        {userRole === UserRoleName.Patient ||
                        userRole === UserRoleName.Provider ||
                        userRole === UserRoleName.Collector ? (
                          <div className={styles.infoHeaderText}>My Record</div>
                        ) : (
                          <div className={styles.infoHeaderText}>My History</div>
                        )}
                      </div>
                    </>
                  )}
                  {userRole === UserRoleName.CareAdvisor && (
                    <>
                      <hr />
                      <div className={styles.infoHeader} onClick={() => roleNavigate(`/scheduleAvailability`)}>
                        <MdOutlineEditCalendar />
                        <div className={styles.infoHeaderText}>Schedule/Availability</div>
                      </div>
                    </>
                  )}
                  {(userRole === UserRoleName.Provider ||
                    userRole === UserRoleName.Collector ||
                    userRole === UserRoleName.CareAdvisor) && (
                    <>
                      <hr />
                      <a
                        href="https://dentistry.one/resources/"
                        style={{ textDecoration: 'none', color: 'unset' }}
                        target="_blank"
                        rel="noreferrer"
                        className="mb-1"
                      >
                        <HiOutlineAcademicCap />
                        <span className={`${styles.infoHeader} ${styles.infoHeaderText}`}>Education</span>
                      </a>
                      <a
                        href="https://dentistry-one-learning.thinkific.com/"
                        style={{ textDecoration: 'none', color: 'unset' }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <GoBook />
                        <span className={`${styles.infoHeader} ${styles.infoHeaderText}`}>Training</span>
                      </a>
                    </>
                  )}
                  {(userRole === UserRoleName.CareAdvisor ||
                    userRole === UserRoleName.Provider ||
                    userRole === UserRoleName.Collector ||
                    userRole === UserRoleName.Admin ||
                    userRole === UserRoleName.SuperAdmin ||
                    userRole === UserRoleName.OrganizationAdmin) && (
                    <>
                      <hr />
                      <div
                        className={styles.infoHeader}
                        onClick={() => {
                          openZendesk();
                        }}
                      >
                        <AiOutlineQuestionCircle />
                        <div className={styles.infoHeaderText}>Help</div>
                      </div>
                    </>
                  )}
                  <hr />
                  <div
                    className={styles.infoHeader}
                    onClick={() => {
                      setLogoutModal(true);
                    }}
                  >
                    <HiOutlineLogout />
                    <div className={styles.infoHeaderText}>Logout</div>
                  </div>
                  <div />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {logoutModal && <Logout setOpenModal={setLogoutModal} />}
      {createNewConv && <CreateNewConversationModal setOpenModal={setCreateNewCon} />}
      {accountSettingsModal && (
        <AccountSettingsModal
          isOpen={accountSettingsModal}
          toggleModal={setAccountSettingsModal}
          onDeleteAccount={onDeleteAccount}
          onChangePassword={handleChangePassword}
          onChangeEmail={handleChangeEmail}
        />
      )}
      {deleteAccountModal && <DeleteAccount setOpenModal={setDeleteAccountModal} />}
      {changePasswordModal && (
        <ChangePasswordModal
          isOpen={changePasswordModal}
          onClose={() => setChangePasswordModal(false)}
          email={userInfoInContext?.email}
        />
      )}
      {changeEmailModal && (
        <ChangeEmailModal
          isOpen={changeEmailModal}
          onClose={() => setChangeEmailModal(false)}
          email={userInfoInContext?.email}
          onSubmit={(email: string) => {
            onSubmitChangeEmail(email);
          }}
        />
      )}
      {verifyEmailModal && (
        <VerifyEmailModal
          isOpen={verifyEmailModal}
          onClose={() => setVerifyEmailModal(false)}
          email={userInfoInContext?.email}
        />
      )}
    </div>
  );
};

export default Sidenav;
